import * as React from 'react';

import { LockOutlined, MailOutlined } from '@ant-design/icons';

import { Input as AInput, Form, message } from 'antd';
import TrackingButton from 'components/TrackingComponents/Button';
import TrackingInput from 'components/TrackingComponents/TrackingInput';
import messages from 'containers/SigninPage/messages';
import useMutationSaga, { GMutation } from 'hooks/useMutationSaga';
import { FormattedMessage } from 'react-intl';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { MutationMeLoginArgs } from 'types/schema';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { meLogin } from '../../utils/query';
import Checkbox from '../Checkbox/Checkbox';
import PageHeader from '../PageHeader/PageHeader';
import topBg from './top-bg.png';

const AntdInput = styled(AInput)<any>``;
const AntdPassword = styled(AInput.Password)<any>``;

const Container = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  background: #ffffff;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  padding: 8pt;
  flex: 1;
  max-width: 720px;
  background-image: url(${topBg});
  background-repeat: no-repeat;
  background-size: cover;
  margin-bottom: 8pt;
  ${media.md`
    padding: 30pt 90pt;
  `};
  p {
    line-height: normal;
    margin: 8px 0;
  }
`;

const RememberRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  margin-bottom: 24px;
`;

const SignUpButton = styled(Form.Item)`
  &.ant-form-item {
    margin-bottom: 12px;
  }
`;

interface SigninFormProps extends RouteComponentProps {
  values?: any;
  onSubmit: (values: any) => void;
}

const SigninForm: React.FC<SigninFormProps> = ({ onSubmit, values, history }) => {
  const [form] = Form.useForm();
  const [signin, { loading }] = useMutationSaga<GMutation<'meLogin'>, MutationMeLoginArgs>(meLogin, {
    authentication: false,
  });

  React.useEffect(() => {
    form.setFieldsValue({
      email: values.email,
      password: values.password,
    });
  }, [values]);
  return (
    <Container>
      <PageHeader size="large">
        <FormattedMessage {...messages.header} />
      </PageHeader>
      <Form
        onFinish={(values) => {
          signin({
            variables: {
              email: values.email,
              password: values.password,
              rememberMe: values.rememberMe,
            },
            authentication: false,
            onSuccess: (response) => {
              localStorage.setItem('token', response?.token);
              localStorage.setItem('rememberToken', response?.rememberMeToken);
              localStorage.setItem('previousLoginEmail', values.email);
              localStorage.setItem('pendingGuestMerge', '1');
              localStorage.setItem('rememberMe', String(values.rememberMe ?? true));
              history.push('/change-store');
            },
            onError: (errors) => {
              if (errors?.length) {
                errors.map((e: any) => message.error(e.message) || 'Đăng nhập thất bại');
              }
            },
          });
        }}
        form={form}
        className="login-form"
        size="large"
        layout="vertical"
      >
        <FormattedMessage {...utilsMessages.email}>
          {(email) => (
            <FormattedMessage {...utilsMessages.fieldEmail}>
              {(fieldEmail) => (
                <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: 'Email' }}>
                  {(fieldRequired) => (
                    <Form.Item
                      name="email"
                      colon={false}
                      required={false}
                      label="Email"
                      hasFeedback
                      rules={[
                        { type: 'email', message: fieldEmail },
                        { required: true, message: fieldRequired },
                      ]}
                      normalize={(value) => value && value.replace(/\s/g, '')}
                    >
                      <TrackingInput
                        prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="Email"
                        autoComplete="username"
                        name="email"
                        options={{
                          action: 'Enter Email',
                          category: 'Signin Form | Input',
                        }}
                      >
                        <AntdInput />
                      </TrackingInput>
                    </Form.Item>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.password}>
          {(password) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: password }}>
              {(fieldRequired) => (
                <Form.Item
                  name="password"
                  colon={false}
                  required={false}
                  label={password}
                  rules={[
                    {
                      required: true,
                      message: fieldRequired,
                    },
                  ]}
                >
                  <TrackingInput
                    name="password"
                    prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    autoComplete="current-password"
                    placeholder={password}
                    options={{
                      action: 'Enter Password',
                      category: 'Signin Form | Input',
                    }}
                  >
                    <AntdPassword />
                  </TrackingInput>
                </Form.Item>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>

        <RememberRow>
          <Form.Item
            name="rememberMe"
            colon={false}
            required={false}
            valuePropName="checked"
            initialValue={localStorage.getItem('rememberMe') === 'true'}
            noStyle={true}
          >
            <Checkbox>
              <FormattedMessage {...messages.rememberMe} />
            </Checkbox>
          </Form.Item>
          <Link to="/forgot-password">
            <FormattedMessage {...messages.forgotPassword} />
          </Link>
        </RememberRow>
        <SignUpButton>
          <TrackingButton
            block
            type="bg-primary"
            htmlType="submit"
            trackingCategory="Signin Form | Button"
            trackingAction="Submit Signin Form"
            loading={loading}
          >
            <FormattedMessage {...messages.header} />
          </TrackingButton>
        </SignUpButton>
        <Link to="/contact-us">
          <TrackingButton block type="primary" trackingCategory="Signin Form | Button" trackingAction="Contact us">
            <FormattedMessage {...messages.signup} />
          </TrackingButton>
        </Link>
      </Form>
    </Container>
  );
};

export default withRouter(SigninForm);
